import type React from "react";
import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import {
  List,
  ListContent,
  ListHeader,
  ListItem,
} from "simplydo/react/ui/list";
import { Popup } from "simplydo/react/ui/popup";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import util from "utils/utils";

import { PermissionGroup } from "./RoleEditor";
import { getRoleIcon } from "./roleIcons";
import type { IRole, RolePermission, RolesUser } from "./types";

export type IRolePermissionConstant = {
  key: string;
  text: string;
  title: string;
};

type RoleTableProps = {
  roles: IRole[];
  permissionGroups: PermissionGroup[];
  getPermissionActions?: (
    roleId: string,
    permission: IRolePermissionConstant,
  ) => React.ReactNode;
  getRoleActions?: (role: IRole) => React.ReactNode;
};

const RoleTable = ({
  roles,
  permissionGroups,
  getPermissionActions,
  getRoleActions,
}: RoleTableProps) => {
  const { t } = useTranslation();
  const getPermissionDetails = useCallback(
    (permissions: string[]) =>
      permissionGroups
        .flatMap((group) =>
          group.permissions.flatMap((category) => category.permissions),
        )
        .filter((permission) => permissions.includes(permission.key)),
    [permissionGroups],
  );

  const permissionsPerRowLimit = 7;
  const hasRoleWithMoreThanRowLimit = useMemo(
    () =>
      !!roles.find((role) => role.permissions.length > permissionsPerRowLimit),
    [roles],
  );

  return (
    <div style={{ maxWidth: "100%", overflow: "auto" }}>
      <Table unstackable compact>
        <TableHeader>
          <TableRow>
            <TableHeaderCell></TableHeaderCell>
            <TableHeaderCell>Role</TableHeaderCell>
            <TableHeaderCell>Permissions</TableHeaderCell>
            {hasRoleWithMoreThanRowLimit ? (
              <TableHeaderCell></TableHeaderCell>
            ) : null}
            <TableHeaderCell>Users</TableHeaderCell>
            <TableHeaderCell></TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {roles.map((role) => {
            const roleActions = getRoleActions ? getRoleActions(role) : null;
            const detailedPermissions = getPermissionDetails(role.permissions);

            const hasWrappingPermissions =
              detailedPermissions?.length > permissionsPerRowLimit * 2;
            const wrappedPermissionCount =
              detailedPermissions?.length - permissionsPerRowLimit * 2;

            return (
              <TableRow key={role._id}>
                <TableCell collapsing>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 5,
                      alignItems: "center",
                    }}
                  >
                    {role.icon ? (
                      <Popup
                        trigger={
                          <Icon
                            icon={getRoleIcon(role.icon.name)}
                            size="large"
                            color={role.icon.colour}
                          />
                        }
                      >{`Custom icon for this role. This icon displays next to users' names in ${util.appName()} if they have this role. You can change this by clicking "Manage".`}</Popup>
                    ) : null}
                    {role.isDefaultRole ? (
                      <Popup
                        trigger={
                          <Label size="small" color="blue">
                            Default role
                          </Label>
                        }
                      >
                        This role is automatically assigned to all users by
                        default.
                      </Popup>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ flex: 1 }}>{role.name}</div>
                    {role.organisations?.length ? (
                      <p>
                        <span>
                          Organisation specific functionality in this role is
                          limited to the following organisations:
                        </span>
                        <List compact variant="bulleted">
                          {role.organisations.map((org) => (
                            <ListItem key={org._id}>
                              <ListContent>
                                <ListHeader>- {org.name}</ListHeader>
                              </ListContent>
                            </ListItem>
                          ))}
                        </List>
                      </p>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell verticalAlign="top">
                  <List compact variant="bulleted">
                    {detailedPermissions
                      .slice(0, permissionsPerRowLimit)
                      .map((permission) => {
                        const permissionActions = getPermissionActions
                          ? getPermissionActions(role._id, permission)
                          : null;
                        return (
                          <ListItem
                            key={permission.key}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            {permissionActions ? (
                              <ListContent floated="right">
                                {permissionActions}
                              </ListContent>
                            ) : null}
                            <Popup
                              trigger={
                                <ListContent>{t(permission.title)}</ListContent>
                              }
                            >
                              {t(permission.text)}
                            </Popup>
                          </ListItem>
                        );
                      })}
                  </List>
                </TableCell>
                {hasRoleWithMoreThanRowLimit ? (
                  <TableCell verticalAlign="top">
                    <List compact variant="bulleted">
                      {detailedPermissions
                        .slice(
                          permissionsPerRowLimit,
                          hasWrappingPermissions
                            ? permissionsPerRowLimit * 2 - 1
                            : permissionsPerRowLimit * 2,
                        )
                        .map((permission) => {
                          const permissionActions = getPermissionActions
                            ? getPermissionActions(role._id, permission)
                            : null;
                          return (
                            <ListItem
                              key={permission.key}
                              style={{ whiteSpace: "nowrap" }}
                            >
                              {permissionActions ? (
                                <ListContent floated="right">
                                  {permissionActions}
                                </ListContent>
                              ) : null}
                              <Popup
                                trigger={
                                  <ListContent>
                                    {t(permission.title)}
                                  </ListContent>
                                }
                              >
                                {t(permission.text)}
                              </Popup>
                            </ListItem>
                          );
                        })}
                      {hasWrappingPermissions ? (
                        <Popup
                          hoverable
                          trigger={
                            <ListItem>
                              <ListContent>
                                + {wrappedPermissionCount} more
                              </ListContent>
                            </ListItem>
                          }
                        >
                          {
                            <List
                              compact
                              variant="bulleted"
                              style={{ maxHeight: 250, overflow: "auto" }}
                            >
                              {getPermissionDetails(role.permissions).map(
                                (permission) => (
                                  <ListItem key={permission.key}>
                                    <ListContent>
                                      {t(permission.title)}
                                    </ListContent>
                                  </ListItem>
                                ),
                              )}
                            </List>
                          }
                        </Popup>
                      ) : null}
                    </List>
                  </TableCell>
                ) : null}
                <TableCell collapsing>
                  {role.isDefaultRole ? "All" : (role.userCount ?? 0)}
                </TableCell>
                <TableCell collapsing>
                  {roleActions ? roleActions : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default RoleTable;
